import { useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowUpRight, AlertTriangle, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { submitPilotLead, finalizePilotLead } from "@/lib/api";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const urgencyStyles = {
  low: { label: "Low urgency", color: "#7dd3a0" },
  medium: { label: "Medium urgency", color: "#e5c454" },
  high: { label: "High urgency", color: "#e56d5a" },
};

// Two mandatory diagnostic probes the real AI Service Writer always appends
// (see backend/server.py system prompt). We include them here so the demo
// experience matches production intake.
const MANDATORY_PROBES = [
  "On a scale of 1-10, how much is this affecting your daily commute?",
  "Is this a Hybrid or EV? (This ensures we have the right Certified Technician and safety equipment ready.)",
];

// Canned illustrative ticket for the /demo-generator sales walkthrough. This
// never touches the backend or the LLM — it just produces something that looks
// realistic enough for a prospect to see the shape of the AI intake.
const DEMO_QUESTION_BANK = {
  work: [
    "When did the issue start, and has it changed since?",
    "Does it happen consistently or only under certain conditions?",
    ...MANDATORY_PROBES,
  ],
  business_casual: [
    "What outcome would make this a clear win for you?",
    "Are there constraints (budget, timing, existing vendors) we should know about?",
    ...MANDATORY_PROBES,
  ],
  business: [
    "What is your desired timeline, and is there any hard deadline?",
    "Have you consulted with anyone else on this matter already?",
    ...MANDATORY_PROBES,
  ],
};

function buildDemoTicket({ business, name, email, phone, description }) {
  const variant = business?.alfred_variant || "business_casual";
  const questions = DEMO_QUESTION_BANK[variant] || DEMO_QUESTION_BANK.business_casual;
  const trimmed = description.trim();
  const firstSentence = trimmed.split(/[.!?]/)[0].trim().slice(0, 140) || trimmed.slice(0, 140);
  return {
    business_slug: business.slug,
    customer_name: name.trim(),
    customer_email: email.trim(),
    customer_phone: phone.trim() || null,
    raw_description: trimmed,
    ai_writeup: {
      issue_summary: firstSentence
        ? `${firstSentence}${firstSentence.endsWith(".") ? "" : "."}`
        : "Customer requested a follow-up.",
      urgency: "medium",
      recommended_service: "Initial intake and consultation",
      prep_questions: questions,
      internal_notes:
        "Demo preview — no real AI call was made. In production Alfred writes a full triaged summary, "
        + "urgency assessment, and prep questions tailored to this exact customer.",
    },
    ai_error: null,
  };
}

// STEP is one of: 'intake' | 'answering' | 'success' | 'success-generic'
export const ServiceWriterForm = ({ business, demoMode = false }) => {
  const [step, setStep] = useState("intake");

  // step 1 form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [submittingIntake, setSubmittingIntake] = useState(false);

  // between step 1 and 2
  const [ticket, setTicket] = useState(null); // full ticket object from step 1

  // step 2 form state
  const [answers, setAnswers] = useState([]); // [{question, answer}]
  const [submittingAnswers, setSubmittingAnswers] = useState(false);

  // step 3 (persisted lead)
  const [finalLead, setFinalLead] = useState(null);

  const accent = business?.accent_color || "#c9a24a";
  const prepQuestions = ticket?.ai_writeup?.prep_questions || [];

  const resetAll = () => {
    setStep("intake");
    setName("");
    setEmail("");
    setPhone("");
    setDescription("");
    setTicket(null);
    setAnswers([]);
    setFinalLead(null);
  };

  const persistWithoutAnswers = async (t) => {
    // Called when AI failed OR prep_questions is empty — skip step 2.
    if (demoMode) {
      // No API — synthesize a saved lead client-side.
      setFinalLead({
        id: `demo-${Math.random().toString(36).slice(2, 10)}`,
        business_slug: business.slug,
        customer_name: t.customer_name,
        customer_email: t.customer_email,
        customer_phone: t.customer_phone,
        raw_description: t.raw_description,
        ai_writeup: t.ai_writeup,
        prep_answers: [],
        created_at: new Date().toISOString(),
      });
      setStep("success-generic");
      toast.success("Demo intake complete.");
      return;
    }
    try {
      const saved = await finalizePilotLead(business.slug, {
        ticket: t,
        prep_answers: [],
      });
      setFinalLead(saved);
      setStep("success-generic");
      toast.success("Received. We'll be in touch.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save your request.");
    }
  };

  const handleIntakeSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name.");
    if (!emailRegex.test(email.trim())) return toast.error("Please enter a valid email.");
    if (description.trim().length < 5)
      return toast.error("Please describe what's going on (a few words at least).");

    setSubmittingIntake(true);
    try {
      let t;
      if (demoMode) {
        // Demo mode — no API, use a canned illustrative ticket so the sales rep
        // can walk a prospect through the full 2-step experience without needing
        // a persisted pilot in the DB.
        await new Promise((r) => setTimeout(r, 900));
        t = buildDemoTicket({ business, name, email, phone, description });
      } else {
        t = await submitPilotLead(business.slug, {
          customer_name: name.trim(),
          customer_email: email.trim(),
          customer_phone: phone.trim() || undefined,
          raw_description: description.trim(),
        });
      }
      setTicket(t);

      const questions = t?.ai_writeup?.prep_questions || [];
      if (questions.length === 0) {
        // AI failed OR returned no questions → skip step 2, persist immediately.
        await persistWithoutAnswers(t);
      } else {
        setAnswers(questions.map((q) => ({ question: q, answer: "" })));
        setStep("answering");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmittingIntake(false);
    }
  };

  const handleAnswersSubmit = async (e) => {
    e.preventDefault();
    setSubmittingAnswers(true);
    try {
      let saved;
      if (demoMode) {
        await new Promise((r) => setTimeout(r, 500));
        saved = {
          id: `demo-${Math.random().toString(36).slice(2, 10)}`,
          business_slug: business.slug,
          customer_name: ticket.customer_name,
          customer_email: ticket.customer_email,
          customer_phone: ticket.customer_phone,
          raw_description: ticket.raw_description,
          ai_writeup: ticket.ai_writeup,
          prep_answers: answers.map((a) => ({ question: a.question, answer: (a.answer || "").trim() })),
          created_at: new Date().toISOString(),
        };
      } else {
        saved = await finalizePilotLead(business.slug, {
          ticket,
          prep_answers: answers.map((a) => ({
            question: a.question,
            answer: (a.answer || "").trim(),
          })),
        });
      }
      setFinalLead(saved);
      setStep("success");
      toast.success(demoMode ? "Demo intake complete." : "Submitted. We wrote it up.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save your answers.");
    } finally {
      setSubmittingAnswers(false);
    }
  };

  // ============ RENDER ============

  return (
    <div data-testid="service-writer-form-wrapper">
      <AnimatePresence mode="wait">
        {step === "intake" && (
          <motion.div
            key="intake"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <StepIndicator current={1} total={2} accent={accent} />
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 mt-4">
              Service Writer · AI intake
            </div>
            <h2
              className="font-serif-display text-white text-2xl md:text-3xl leading-snug mb-2"
              data-testid="service-writer-title"
            >
              Tell us what&apos;s going on.
            </h2>
            <p className="font-mono-plex text-[13px] text-white/45 mb-6 max-w-lg leading-relaxed">
              Describe the issue in plain English. Our AI writes it up into a structured ticket so{" "}
              {business.name} can respond fast and prepared.
            </p>

            <form
              onSubmit={handleIntakeSubmit}
              className="space-y-3"
              data-testid="service-writer-form"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submittingIntake}
                  data-testid="service-writer-name"
                  aria-label="Your name"
                  className="rounded-none border-white/15 bg-white/[0.02] text-white placeholder:text-white/30 font-mono-plex text-sm tracking-wide h-12 px-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50"
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submittingIntake}
                  data-testid="service-writer-email"
                  aria-label="Your email"
                  className="rounded-none border-white/15 bg-white/[0.02] text-white placeholder:text-white/30 font-mono-plex text-sm tracking-wide h-12 px-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50"
                />
              </div>

              <Input
                type="tel"
                placeholder="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={submittingIntake}
                data-testid="service-writer-phone"
                aria-label="Your phone number"
                className="rounded-none border-white/15 bg-white/[0.02] text-white placeholder:text-white/30 font-mono-plex text-sm tracking-wide h-12 px-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50"
              />

              <Textarea
                placeholder="Describe what's happening. Include any dashboard warning lights or specific computer messages you're seeing."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={submittingIntake}
                rows={5}
                data-testid="service-writer-description"
                aria-label="Describe your issue"
                className="rounded-none border-white/15 bg-white/[0.02] text-white placeholder:text-white/30 font-mono-plex text-sm leading-relaxed tracking-wide px-4 py-3 min-h-[140px] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50 resize-none"
              />

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                <p className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-white/30">
                  No spam · No obligation · Pilot service
                </p>
                <Button
                  type="submit"
                  disabled={submittingIntake}
                  data-testid="service-writer-submit"
                  className="rounded-none h-12 px-6 bg-white text-black hover:bg-white/85 font-mono-plex text-[11px] uppercase tracking-[0.24em] font-medium inline-flex items-center gap-2 disabled:opacity-70"
                  style={submittingIntake ? {} : { boxShadow: `inset 0 -2px 0 0 ${accent}` }}
                >
                  {submittingIntake ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Writing it up…</span>
                    </>
                  ) : (
                    <>
                      <span>Send to Service Writer</span>
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {step === "answering" && ticket && (
          <motion.div
            key="answering"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            data-testid="service-writer-step2"
          >
            <StepIndicator current={2} total={2} accent={accent} />

            <div className="mt-4 mb-6">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                Draft ticket · pending your confirmation
              </div>
              <TicketPreview writeup={ticket.ai_writeup} accent={accent} />
            </div>

            <h2
              className="font-serif-display text-white text-2xl md:text-3xl leading-snug mb-2"
              data-testid="service-writer-questions-title"
            >
              A few quick questions.
            </h2>
            <p className="font-mono-plex text-[13px] text-white/45 mb-6 max-w-lg leading-relaxed">
              Answering these helps {business.name} triage before you even walk in. Skip any you&apos;re
              not sure about.
            </p>

            <form
              onSubmit={handleAnswersSubmit}
              className="space-y-6"
              data-testid="service-writer-answers-form"
              noValidate
            >
              {answers.map((a, i) => (
                <div key={i} data-testid={`service-writer-question-${i}`}>
                  <label
                    className="font-mono-plex text-[11px] uppercase tracking-[0.24em] text-white/60 flex items-baseline gap-3 mb-2"
                    htmlFor={`prep-answer-${i}`}
                  >
                    <span className="text-white/35 text-[10px]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="normal-case tracking-normal font-serif-display text-white/85 text-base leading-snug">
                      {a.question}
                    </span>
                  </label>
                  <Textarea
                    id={`prep-answer-${i}`}
                    value={a.answer}
                    onChange={(e) => {
                      const next = [...answers];
                      next[i] = { ...next[i], answer: e.target.value };
                      setAnswers(next);
                    }}
                    disabled={submittingAnswers}
                    rows={2}
                    placeholder="Your answer…"
                    data-testid={`service-writer-answer-input-${i}`}
                    className="rounded-none border-white/15 bg-white/[0.02] text-white placeholder:text-white/30 font-mono-plex text-sm leading-relaxed tracking-wide px-4 py-3 min-h-[64px] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50 resize-none"
                  />
                </div>
              ))}

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={resetAll}
                  disabled={submittingAnswers}
                  className="font-mono-plex text-[11px] uppercase tracking-[0.28em] text-white/45 hover:text-white/90 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="service-writer-back"
                >
                  <ArrowLeft className="h-3 w-3" /> Start over
                </button>
                <Button
                  type="submit"
                  disabled={submittingAnswers}
                  data-testid="service-writer-answers-submit"
                  className="rounded-none h-12 px-6 bg-white text-black hover:bg-white/85 font-mono-plex text-[11px] uppercase tracking-[0.24em] font-medium inline-flex items-center gap-2 disabled:opacity-70"
                  style={submittingAnswers ? {} : { boxShadow: `inset 0 -2px 0 0 ${accent}` }}
                >
                  {submittingAnswers ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Sending…</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Answers</span>
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {step === "success" && finalLead && (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="border border-white/15 bg-white/[0.02] p-6 sm:p-8"
            data-testid="service-writer-result"
          >
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="h-4 w-4" style={{ color: accent }} />
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/50">
                Ticket #{finalLead.id.slice(0, 8).toUpperCase()} · Sent
              </div>
            </div>
            <h3 className="font-serif-display text-white text-2xl md:text-3xl leading-snug mb-4">
              Thanks{finalLead.customer_name ? `, ${finalLead.customer_name.split(" ")[0]}` : ""}.
              <br />
              We got everything.
            </h3>
            <p className="font-mono-plex text-[13px] text-white/55 leading-relaxed mb-6 max-w-lg">
              A copy of your write-up and answers has been sent to {business.name}. They&apos;ll be in
              touch shortly to schedule and confirm details.
            </p>

            {finalLead.ai_writeup && (
              <div className="mt-6">
                <TicketPreview writeup={finalLead.ai_writeup} accent={accent} />
              </div>
            )}

            {finalLead.prep_answers?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/35 mb-3">
                  Your answers
                </div>
                <ul className="space-y-3" data-testid="service-writer-answers-recap">
                  {finalLead.prep_answers.map((pa, i) => (
                    <li key={i} className="font-mono-plex text-[12px] leading-relaxed">
                      <div className="text-white/60">
                        <span className="text-white/30">
                          {String(i + 1).padStart(2, "0")}
                        </span>{" "}
                        {pa.question}
                      </div>
                      <div className="text-white/85 mt-1 pl-6">
                        {pa.answer || <span className="text-white/30 italic">(skipped)</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="button"
              onClick={resetAll}
              className="mt-8 font-mono-plex text-[11px] uppercase tracking-[0.28em] text-white/50 hover:text-white/90 transition-colors"
              data-testid="service-writer-reset"
            >
              ← Submit another request
            </button>
          </motion.div>
        )}

        {step === "success-generic" && (
          <motion.div
            key="success-generic"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="border border-white/15 bg-white/[0.02] p-6 sm:p-8"
            data-testid="service-writer-result-generic"
          >
            <div className="border border-yellow-500/20 bg-yellow-500/5 p-4 flex items-start gap-3 mb-6">
              <AlertTriangle className="h-4 w-4 text-yellow-500/80 mt-0.5 shrink-0" />
              <div>
                <div className="font-mono-plex text-[11px] uppercase tracking-[0.24em] text-yellow-500/80 mb-1">
                  Received — write-up pending
                </div>
                <p className="font-mono-plex text-[12px] text-white/60 leading-relaxed">
                  Your request came through. Our AI write-up is temporarily unavailable, but the
                  shop has your details and will reach out directly.
                </p>
              </div>
            </div>
            <h3 className="font-serif-display text-white text-2xl md:text-3xl leading-snug mb-3">
              Thanks. We&apos;ll be in touch.
            </h3>
            <p className="font-mono-plex text-[13px] text-white/55 leading-relaxed">
              {business.name} has your details. Expect a response shortly.
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-8 font-mono-plex text-[11px] uppercase tracking-[0.28em] text-white/50 hover:text-white/90 transition-colors"
              data-testid="service-writer-reset"
            >
              ← Submit another request
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------- Sub-components ----------

const StepIndicator = ({ current, total, accent }) => (
  <div
    className="flex items-center gap-2 font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40"
    data-testid="service-writer-step-indicator"
  >
    <span className="text-white/70">Step {current}</span>
    <span className="text-white/20 ml-1">of {total}</span>
    <div className="flex items-center gap-1.5 ml-2">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className="h-[2px] w-6 transition-colors"
          style={{
            background: i < current ? accent : "rgba(255,255,255,0.15)",
          }}
        />
      ))}
    </div>
  </div>
);

const TicketPreview = ({ writeup, accent }) => {
  if (!writeup) return null;
  const u = urgencyStyles[writeup.urgency] || { label: writeup.urgency, color: accent };
  return (
    <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="service-writer-ticket-preview">
      <div
        className="inline-flex items-center gap-2 border px-2.5 py-1 font-mono-plex text-[10px] uppercase tracking-[0.28em]"
        style={{ borderColor: `${u.color}66`, color: u.color }}
        data-testid="service-writer-urgency"
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: u.color }} />
        {u.label}
      </div>
      <p
        className="mt-3 font-serif-display text-white/90 text-lg md:text-xl leading-snug"
        data-testid="service-writer-summary"
      >
        {writeup.issue_summary}
      </p>
      {writeup.recommended_service && (
        <div className="mt-3 font-mono-plex text-[12px] text-white/60" data-testid="service-writer-recommended">
          → {writeup.recommended_service}
        </div>
      )}
    </div>
  );
};

export default ServiceWriterForm;
